import * as React from "react";
import { cn } from "@/lib/utils";

interface SelectV2Props extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  helperText?: string;
  errorText?: string;
  options?: Array<{ value: string; label: string; disabled?: boolean }>;
}

export function SelectV2({
  label,
  helperText,
  errorText,
  options,
  className,
  children,
  id,
  "aria-describedby": externalDescribedBy,
  ...props
}: SelectV2Props) {
  const generatedId = React.useId();
  const selectId = id ?? generatedId;
  const errorId = errorText ? `${selectId}-error` : undefined;
  const helperId = !errorText && helperText ? `${selectId}-helper` : undefined;
  const describedBy =
    [externalDescribedBy, errorId, helperId].filter(Boolean).join(" ") ||
    undefined;

  return (
    <div className="block space-y-1.5">
      {label ? (
        <label
          htmlFor={selectId}
          className="text-xs font-semibold text-[var(--v2-text-strong)]"
        >
          {label}
        </label>
      ) : null}
      <select
        id={selectId}
        aria-describedby={describedBy}
        aria-invalid={errorText ? true : undefined}
        className={cn(
          "v2-focus-ring min-h-10 w-full rounded-[var(--v2-radius-md)] border border-[var(--v2-border)] bg-white px-3 text-sm text-[var(--v2-text-strong)]",
          errorText ? "border-red-300" : "",
          className
        )}
        {...props}
      >
        {children ||
          options?.map((option) => (
            <option
              key={`${option.value}-${option.label}`}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
      </select>
      {errorText ? (
        <span id={errorId} className="text-xs text-red-600" role="alert">
          {errorText}
        </span>
      ) : null}
      {!errorText && helperText ? (
        <span id={helperId} className="text-xs text-[var(--v2-text-muted)]">
          {helperText}
        </span>
      ) : null}
    </div>
  );
}
